import { useAppDispatch } from '../../../../hooks/redux';
import { notificationService } from '../../../../services/NotificationService';
import {
  markNotificationAsReadStart,
  markNotificationAsReadSuccess,
  markNotificationAsReadFailure
} from '../../../../store/slices/notificationSlice';
import { NotificationType } from '../../../../types/Notification.types';
import { NotificationIcon } from './NotificationIcon';
import { formatFullDate } from '../../../../utils/Date.util';
import { Tooltip } from '../../../ui/Tooltip/Tooltip';
import { Link } from 'react-router-dom';

type NotificationProps = Readonly<{
  id: string;
  name: string;
  title: string;
  description: string;
  date: string;
  relatedAISystem: string;
  onRedirectAction?: () => void;
  relatedAiSystemId?: string;
  type?: NotificationType;
  actorName?: string | null;
  isUnread?: boolean;
}>;

export default function Notification(NotifyProps: NotificationProps) {
  const dispatch = useAppDispatch();

  const isRedirectable =
    NotifyProps.type === 'ALERT' && NotifyProps.relatedAiSystemId;

  const handleMarkAsRead = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!NotifyProps.isUnread) return;

    try {
      dispatch(markNotificationAsReadStart());
      await notificationService.markNotificationAsRead(NotifyProps.id);
      dispatch(markNotificationAsReadSuccess(NotifyProps.id));
    } catch (error: any) {
      dispatch(
        markNotificationAsReadFailure(
          error.message || 'Failed to mark notification as read'
        )
      );
    }
  };

  const NotificationWrapper = ({ children }: { children: React.ReactNode }) => {
    const className = `flex p-2 items-start gap-2 self-stretch ${
      isRedirectable ? 'hover:bg-gray-50 cursor-pointer rounded-md' : ''
    }`;

    if (isRedirectable) {
      return (
        <Link
          to={`/ai-systems/${NotifyProps.relatedAiSystemId}/monitoring-audit-logging`}
          className={className}
          onClick={NotifyProps.onRedirectAction}
        >
          {children}
        </Link>
      );
    }

    return <div className={className}>{children}</div>;
  };

  return (
    <NotificationWrapper>
      <div className="flex items-start gap-2 flex-1 max-w-full">
        <NotificationIcon
          type={NotifyProps.type || 'INFORMATIVE'}
          actorName={NotifyProps.actorName}
        />
        <div
          className={`flex flex-col items-start gap-1 flex-1 text-gray-550 text-sm ml-1 max-w-[310px] ${!NotifyProps.isUnread ? 'mr-5' : ''}`}
        >
          <p className="font-medium text-gray-900v2 max-w-full whitespace-normal break-words">
            {NotifyProps.title}
          </p>
          <p className="max-w-full whitespace-normal break-words">
            {NotifyProps.description}
          </p>
          <div className="flex items-center gap-3 mt-3 text-xs">
            <span>{formatFullDate(NotifyProps.date)}</span>
            <div className="w-[6px] h-[6px] rounded-full bg-gray-200"></div>
            <span>{NotifyProps.relatedAISystem}</span>
          </div>
        </div>
        {NotifyProps.isUnread && (
          <Tooltip
            content="Mark as read"
            position="left"
            showArrow={false}
            className="mr-[8px]"
          >
            <button
              onClick={handleMarkAsRead}
              className="w-3 h-3 bg-orange-500 rounded-full hover:bg-orange-600 transition-colors duration-200 border border-white hover:outline hover:outline-2 hover:outline-orange-600"
              aria-label="Mark as read"
            ></button>
          </Tooltip>
        )}
      </div>
    </NotificationWrapper>
  );
}
